import { Link } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FilamentComparePanel from '../../../components/feature/FilamentComparePanel';
import { FilamentStressStrainChart } from '../../../components/feature/FilamentStressStrainChart';
import { getFilamentMaker, getFilamentColor } from '../../../data/filamentTestData';
import { useFilamentText } from '../../../hooks/useFilamentText';
import BendingStrengthCard from '../../../components/feature/BendingStrengthCard';

const ABSGFPage = () => {
  const { isEnglish, getMaterialData, normalizedSlug, ...TEXT } = useFilamentText('abs-gf');
  const materialData = getMaterialData('abs-gf');

  const breadcrumbItems = [
    { label: isEnglish ? 'Home' : 'ホーム', path: '/' },
    { label: isEnglish ? 'Filament Data' : 'フィラメントデータ', path: '/filament' },
    { label: 'ABS', path: '/filament/abs' },
    { label: 'ABS-GF', path: '/filament/abs-gf' }
  ];

  const filamentId = 'abs-gf';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8" data-lang={isEnglish ? 'en' : 'ja'} data-filament-key={normalizedSlug}>
            {/* タイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                {isEnglish ? 'ABS-GF (Glass Fiber Reinforced ABS)' : 'ABS-GF（ガラス繊維強化ABS）'}
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 比較指標 */}
            <FilamentComparePanel slug="abs-gf" />

            {/* 特徴 */}
            <div className="metal-panel p-8 border-l-4 border-accent">
              <h2 className="text-3xl font-semibold text-white font-['Exo'] mb-6 accent-line pb-3">
                {TEXT.features}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="text-metal-light space-y-3 text-lg">
                  {materialData?.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-400 mt-1"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <ul className="text-metal-light space-y-3 text-lg">
                  {materialData?.features.slice(3).map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-400 mt-1"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 基本情報 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.classification}</span>
                      <span className="difficulty-advanced px-3 py-1 rounded text-sm font-semibold">{TEXT.advanced}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.baseResin}</span>
                      <span className="text-white">{materialData?.baseResin}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.meltingPoint}</span>
                      <span className="text-white">220-240°C</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.density}</span>
                      <span className="text-white">1.15-1.20 g/cm³</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.shrinkageRate}</span>
                      <span className="text-white">0.3-0.5%</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.heatResistance}</span>
                      <span className="text-white">95-105°C</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 推奨設定 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.recommendedSettings}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#3a3d42] text-white">
                      <th className="px-4 py-3 text-left">{TEXT.item}</th>
                      <th className="px-4 py-3 text-left">{TEXT.recommendedValue}</th>
                      <th className="px-4 py-3 text-left">{TEXT.notes}</th>
                    </tr>
                  </thead>
                  <tbody className="text-metal-light">
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleTemp}</td>
                      <td className="px-4 py-3 text-white">245-265°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Higher than standard ABS' : '通常ABSより高め'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.bedTemp}</td>
                      <td className="px-4 py-3 text-white">95-110°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'High temp to prevent warping' : '反り防止のため高温'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.fanSetting}</td>
                      <td className="px-4 py-3 text-white">0-20%</td>
                      <td className="px-4 py-3">{isEnglish ? 'Prioritize layer adhesion' : '層間接着を優先'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.printSpeed}</td>
                      <td className="px-4 py-3 text-white">30-50mm/s</td>
                      <td className="px-4 py-3">{isEnglish ? 'Quality focused' : '品質重視'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleDiameter}</td>
                      <td className="px-4 py-3 text-white">{isEnglish ? '0.4mm or larger' : '0.4mm以上'}</td>
                      <td className="px-4 py-3">{isEnglish ? 'Hardened steel required' : '硬化スチール製必須'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">{TEXT.caution}</td>
                      <td className="px-4 py-3 text-white" colSpan={2}>{isEnglish ? 'Enclosed chamber recommended, ventilation required' : '密閉チャンバー推奨、換気必須'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 曲げ強度 */}
            <BendingStrengthCard slug={filamentId} isEnglish={isEnglish} />

            <FilamentStressStrainChart 
              filamentId={filamentId}
              filamentName="ABS-GF"
            />

            {/* 使用例 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.useCases}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Suitable Applications' : '向いている造形'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    {materialData?.suitableUses.map((use, idx) => (
                      <li key={idx}>• {use}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Pros & Cons' : '長所・短所'}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-green-400">{isEnglish ? 'Pros:' : '長所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.pros}</span>
                    </div>
                    <div>
                      <span className="text-red-400">{isEnglish ? 'Cons:' : '短所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.cons}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.precautions}
              </h2>
              <div className="text-metal-light space-y-2">
                {materialData?.precautions.map((precaution, idx) => (
                  <p key={idx}>• {precaution}</p>
                ))}
              </div>
            </div>

            {/* 戻るリンク */}
            <div className="text-center mt-8">
              <Link 
                to="/filament/abs" 
                className="inline-flex items-center space-x-2 text-accent hover:text-accent-orange transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                <span>{isEnglish ? 'Back to ABS Details' : 'ABS詳細ページに戻る'}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ABSGFPage;
